import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getWishList } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import updateGetWishListsParameter from "@insite/client-framework/Store/Components/AddToListModal/Handlers/UpdateGetWishListsParameter";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import loadWishLists from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { BrandModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import ScheduleReminderModal, {
    ScheduleReminderModalStyles,
} from "@insite/content-library/Components/ScheduleReminderModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import {
    addAllToWishlistDataLayer,
    addAllToWishListDataLayerOrderPage,
} from "@tCooneyBrothers/Components/AddToWishListDataLayer";
import debounce from "lodash/debounce";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: AddToListModalStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const selectedCategoryPath = getSelectedCategoryPath(state);
    const pathnameLowerCase = location.pathname.toLowerCase();
    const path =
        selectedCategoryPath ||
        (pathnameLowerCase.startsWith("/content/") || pathnameLowerCase.startsWith("/.spire/")
            ? ""
            : location.pathname);
    const catalogPage = getCatalogPageStateByPath(state, path).value;
    const cartState = getCurrentCartState(state).value;
    return {
        session: state.context.session,
        modalIsOpen: state.components.addToListModal.isOpen,
        productInfos: state.components.addToListModal.productInfos,
        getWishListsParameter: state.components.addToListModal.getWishListsParameter,
        wishListsDataView: getWishListsDataView(state, state.components.addToListModal.getWishListsParameter),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url.toLowerCase(),
        location: getLocation(state),
        productListCatalogPage: getCatalogPageStateByPath(state, path).value,
        productsData: state.data.products.byId,
        category: getCategoryState(state, catalogPage?.categoryIdWithBrandId ?? catalogPage?.categoryId).value,
        cartState,
        cartStateUpdated: state.data.carts.byId,
        cartId: state.pages.savedOrderDetails.cartId,
        orderCartId: state.pages.orderConfirmation.cartId,
        orderNumber: state.pages.orderDetails.orderNumber,
        orderInfo: state.data.orders.byId,
    };
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    updateGetWishListsParameter,
    loadWishLists,
};

export interface AddToListModalStyles {
    modal?: ModalPresentationProps;
    innerWrapper?: InjectableCss;
    signInMessageText?: TypographyProps;
    descriptionText?: TypographyProps;
    listsSelect?: DynamicDropdownPresentationProps;
    newListInput?: TextFieldProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    addButton?: ButtonPresentationProps;
    linkLabel?: InjectableCss;
    cancelButtonGridItem?: GridItemProps;
    addButtonGridItem?: GridItemProps;
    bottomRowGridItem?: GridItemProps;
    container?: GridContainerProps;
    scheduleReminderCheckbox?: CheckboxPresentationProps;
    scheduleReminderModal?: ScheduleReminderModalStyles;
}

export const addToListModalStyles: AddToListModalStyles = {
    modal: {
        size: 350,
        cssOverrides: {
            modalBody: css`
                overflow-y: visible;
            `,
            modalTitle: css`
                padding: 10px 20px;
            `,
            modalContent: css`
                padding: 20px;
            `,
        },
    },
    container: {
        gap: 10,
        css: css`
            display: flex;
            > [class^="GridOffset"] {
                align-items: flex-end;
            }
        `,
    },
    descriptionText: {
        as: "p",
    },
    listsSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 15px;
            `,
        },
    },
    newListInput: {
        cssOverrides: {
            formField: css`
                margin-top: 15px;
            `,
        },
    },
    buttonsWrapper: {
        css: css`
            margin-top: 30px;
            text-align: right;
        `,
    },
    bottomRowGridItem: {
        width: 12,
        css: css`
            justify-content: space-between;
            margin-top: 30px;
        `,
    },
    cancelButton: {
        variant: "secondary",
        css: css`
            padding-top: 20px;
        `,
    },
    cancelButtonGridItem: {
        width: [6, 6, 6, 6, 6],
        css: css`
            justify-content: flex-start;
        `,
    },
    addButtonGridItem: {
        width: [6, 6, 6, 6, 6],
        css: css`
            justify-content: flex-end;
        `,
    },
    addButton: {
        css: css`
            margin-top: 10px;
        `,
    },
    scheduleReminderCheckbox: {
        css: css`
            margin-top: 15px;
            align-items: flex-start;
        `,
    },
};

type DataLayerItem = {
    item_id: string;
    item_name: string;
    index: number;
    item_brand: string | BrandModel;
    item_category: string;
    price: number | undefined;
    quantity: number | undefined;
    item_list_id?: string;
    item_list_name?: string;
};

type DataCartLayerItem = {
    item_id: string;
    item_name: string;
    item_brand: string;
    price?: number;
    quantity: number | null;
    [key: string]: any;
};
export const LastUpdatedListIdCookieName = "LastUpdatedListId";

const AddToListModal = ({
    productInfos,
    session,
    modalIsOpen,
    getWishListsParameter,
    wishListsDataView,
    signInUrl,
    location,
    setAddToListModalIsOpen,
    addToWishList,
    updateGetWishListsParameter,
    loadWishLists,
    extendedStyles,
    productListCatalogPage,
    cartState,
    productsData,
    category,
    cartId,
    cartStateUpdated,
    orderCartId,
    orderInfo,
    orderNumber,
}: Props) => {
    const [styles] = useState(() => mergeToNew(addToListModalStyles, extendedStyles));

    const toasterContext = useContext(ToasterContext);

    const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    const [selectedWishListId, setSelectedWishListId] = useState("new");
    const [newListName, setNewListName] = useState("");
    const [selectListError, setSelectListError] = useState<React.ReactNode>("");
    const [newListNameError, setNewListNameError] = useState<React.ReactNode>("");
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [inProgress, setInProgress] = useState(false);
    const [isScheduled, setIsScheduled] = useState(false);
    const [scheduleReminderModalIsOpen, setScheduleReminderModalIsOpen] = useState(false);
    const [currentWishList, setCurrentWishList] = useState<WishListModel>();

    useEffect(() => {
        if (!modalIsOpen) {
            return;
        }

        const lastUpdatedListId = getCookie(LastUpdatedListIdCookieName) || "new";
        setSelectedWishListId("new");
    }, [modalIsOpen]);

    useEffect(() => {
        if (modalIsOpen && !wishListsDataView.value && !wishListsDataView.isLoading) {
            loadWishLists(getWishListsParameter);
        }
    }, [getWishListsParameter]);

    useEffect(() => {
        if (wishListsDataView.value) {
            let options: OptionObject[] = [{ optionText: translate("Create New List"), optionValue: "new" }];
            options = options.concat(
                wishListsDataView.value
                    .filter(o => !o.isAutogenerated)
                    .map(o => ({ optionText: o.name, optionValue: o.id })),
            );
            setOptions(options);
        }
    }, [wishListsDataView]);

    if (!productInfos) {
        return null;
    }

    const modalCloseHandler = () => {
        setNewListName("");
        setSelectListError("");
        setNewListNameError("");
        setInProgress(false);
        setAddToListModalIsOpen({ modalIsOpen: false });
    };

    const listChangeHandler = (value?: string) => {
        setSelectedWishListId(value || "new");
        setSelectListError("");
    };

    const debouncedUpdateParameter = debounce((value: string) => {
        updateGetWishListsParameter({
            ...getWishListsParameter,
            query: value || undefined,
        });
    }, 200);

    const listsInputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        debouncedUpdateParameter(event.currentTarget.value);
    };

    const newListInputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewListName(event.target.value);
        let error = "";
        if (!event.target.value) {
            error = siteMessage("Lists_Enter_New_Wishlist_Name") as string;
        } else if (event.target.value.length > 100) {
            error = siteMessage("Lists_List_Name_Too_Long", "100") as string;
        }
        setNewListNameError(error);
    };
    const scheduleReminderChangeHandler = (_: React.SyntheticEvent<Element, Event>, value: boolean) => {
        setIsScheduled(!isScheduled);
    };

    const closeScheduleReminderModalHandler = () => {
        setScheduleReminderModalIsOpen(false);
        setIsScheduled(false);
        modalCloseHandler();
    };

    const generateItemsList = (productInfos: any) => {
        const productData = productsData[productInfos[0].productId];
        const items: DataLayerItem[] = [];
        if (productData) {
            const categoryArr = productListCatalogPage?.uri?.split("?")[1]?.split("/") || [];
            const categories = categoryArr?.slice(1, categoryArr.length);
            const categoryArr1 = productListCatalogPage?.breadCrumbs || [];
            const categories1 = categoryArr1.slice(4, categoryArr1?.length - 1).map(cat => cat.text);
            const categorys = categories.length === 0 ? "Search" : category?.shortDescription;
            items.push({
                item_id: productData.productNumber || "",
                item_name: productData.productTitle || "",
                index: 0,
                item_brand: productData.brand?.name || "",
                item_category: categorys || categories1[0] || productData.urlSegment,
                price: productInfos[0].pricing?.actualPrice,
                quantity: productInfos[0].qtyOrdered,
                item_list_id: productData.id || "",
                item_list_name: categorys || categories1[0] || productData.urlSegment,
            });
        }
        return items;
    };

    const generateSingleItemArray = (productInfos: any) => {
        const itemsArr = [];
        if (cartState?.cartLines) {
            const cartLine = cartState?.cartLines.filter(cartline => cartline.productId === productInfos[0].productId);

            const obj: DataCartLayerItem = {
                item_id: cartLine[0].erpNumber || "",
                item_name: cartLine[0].shortDescription || "",
                item_brand: cartLine[0].brand?.name || "",
                price: cartLine[0].pricing?.unitNetPrice,
                quantity: productInfos[0].qtyOrdered,
                item_list_id: cartLine[0].productId || "",
                item_list_name: cartLine[0].properties.categories || "",
                item_category: cartLine[0].properties.categories || "",
            };
            // const categoriesArr: string[] =
            //     cartState.cartLines[0].properties && cartState.cartLines[0].properties.categories
            //         ? cartState.cartLines[0].properties.categories.split(",")
            //         : [];
            // let i = 1;
            // if (categoriesArr[categoriesArr.length - 1] === "") {
            //     categoriesArr.pop();
            // }
            // if (categoriesArr.length !== 0) {
            //     const uniqueItems: Record<string, boolean> = {};
            //     console.log("unique item ", uniqueItems);
            //     for (const item of categoriesArr) {
            //         if (!uniqueItems[item]) {
            //             if (i === 1) {
            //                 obj.item_category = item;
            //                 uniqueItems[item] = true;
            //             } else {
            //                 obj[`item_category${i}`] = item;
            //                 uniqueItems[item] = true;
            //             }
            //             i++;
            //         }
            //     }
            // }
            itemsArr.push(obj);
        }
        // console.log("items Array", itemsArr);
        return itemsArr;
    };

    const generateMultipleItemArray = (productInfos: any) => {
        const itemsArr: DataCartLayerItem[] = [];
        if (cartState?.cartLines) {
            cartState?.cartLines.forEach(cartLine => {
                const obj: DataCartLayerItem = {
                    item_id: cartLine.erpNumber || "",
                    item_name: cartLine?.shortDescription || "",
                    item_brand: cartLine.brand?.name || "",
                    price: cartLine.pricing?.unitNetPrice,
                    quantity: cartLine.qtyOrdered,
                    item_list_id: cartLine.productId || "",
                    item_list_name: cartLine.properties.categories || "",
                    item_category: cartLine.properties.categories || "",
                };
                // const categoriesArr: string[] =
                //     cartLine && cartLine.properties && cartLine.properties.categories
                //         ? cartLine.properties.categories.split(",")
                //         : [];
                // let i = 1;
                // if (categoriesArr[categoriesArr.length - 1] === "") {
                //     categoriesArr.pop();
                // }
                // if (categoriesArr.length !== 0) {
                //     const uniqueItems: Record<string, boolean> = {};
                //     for (const item of categoriesArr) {
                //         if (!uniqueItems[item]) {
                //             if (i === 1) {
                //                 obj.item_category = item;
                //                 uniqueItems[item] = true;
                //             } else {
                //                 obj[`item_category${i}`] = item;
                //                 uniqueItems[item] = true;
                //             }
                //             i++;
                //         }
                //     }
                // }
                itemsArr.push(obj);
            });
        }
        return itemsArr;
    };

    const generateDataLayer: any = (wishlistItems: DataCartLayerItem[] | null) => {
        const dataLayer = (window as any).dataLayer || [];
        if (dataLayer) {
            dataLayer.push({ ecommerce: null });
            dataLayer.push({
                event: "add_to_wishlist",
                ecommerce: {
                    wishlistItems,
                },
            });
        }
        // console.log("add to whishlist1 datalayer", dataLayer);
        // return dataLayer;
    };
    const addToDataLayer = (productInfos: any) => {
        const wishlistItems = generateItemsList(productInfos);
        const dataLayer = (window as any).dataLayer || [];
        if (dataLayer) {
            dataLayer.push({
                event: "add_to_wishlist",
                ecommerce: {
                    wishlistItems,
                },
            });
        }
        // console.log("dataLayer2", dataLayer);
        // return dataLayer;
    };

    const addToListDataLayer = (productInfos: any) => {
        const wishlistItems = generateSingleItemArray(productInfos);
        return generateDataLayer(wishlistItems);
    };

    const addAllToListDataLayer = (productInfos: any) => {
        const wishlistItems = generateMultipleItemArray(productInfos);
        return generateDataLayer(wishlistItems);
    };

    const addToListButtonClickHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();

        if (!selectedWishListId || !options.some(o => o.optionValue === selectedWishListId)) {
            setSelectListError(siteMessage("Lists_Enter_New_Wishlist_Name"));
            return;
        }

        const isNewList = selectedWishListId === "new";
        if (isNewList && !newListName) {
            setNewListNameError(siteMessage("Lists_Enter_New_Wishlist_Name"));
            return;
        }
        
        if (isNewList && newListName && newListName.length > 100) {
            setNewListNameError(siteMessage("Lists_List_Name_Too_Long", "100"));
            return;
        }

        setNewListNameError("");
        setSelectListError("");

        setInProgress(true);

        addToWishList({
            productInfos,
            selectedWishList: isNewList ? undefined : wishListsDataView.value?.find(o => o.id === selectedWishListId),
            newListName,
            onSuccess: async (wishList: WishListModel) => {
                setCookie(LastUpdatedListIdCookieName, wishList.id);
                setSelectedWishListId(wishList.id);
                toasterContext.addToast({
                    body: siteMessage("Lists_ProductAdded"),
                    messageType: "success",
                    isFocusAfterMount: true,
                });
                if (isScheduled) {
                    const updatedWishList = await getWishList({ wishListId: wishList.id, expand: ["schedule"] });
                    setCurrentWishList(updatedWishList);
                    setScheduleReminderModalIsOpen(true);
                } else {
                    modalCloseHandler();
                }
            },
            onError: errorMessage => {
                setInProgress(false);
                if (isNewList) {
                    setNewListNameError(errorMessage);
                } else {
                    modalCloseHandler();
                    toasterContext.addToast({ body: errorMessage, messageType: "danger" });
                }
            },
            onComplete(wishListProps) {
                if (wishListProps?.result?.wishList) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(wishListProps?.result?.wishList);
                } else if (wishListProps?.result?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(wishListProps?.result?.errorMessage);
                }
            },
        });

        if (productInfos[0].pricing) {
            addToDataLayer(productInfos);
        } else if (window.location.pathname.toLowerCase() === "/cart") {
            if (productInfos.length === 1) {
                addToListDataLayer(productInfos);
            } else {
                addAllToListDataLayer(productInfos);
            }
        } else {
            // eslint-disable-next-line no-lonely-if
            if (window.location.href.includes("OrderConfirmation")) {
                if (orderCartId && cartStateUpdated[orderCartId].cartLines) {
                    addAllToWishlistDataLayer(cartStateUpdated[orderCartId].cartLines);
                }
            } else if (window.location.href.includes("saved-carts")) {
                if (productInfos.length === 1 && cartId && cartStateUpdated[cartId].cartLines) {
                    const currentList = cartStateUpdated[cartId].cartLines?.slice();
                    const updatedList = currentList?.filter(item => item.productId === productInfos[0].productId);
                    addAllToWishlistDataLayer(updatedList);
                } else {
                    // eslint-disable-next-line no-lonely-if
                    if (cartId && cartStateUpdated[cartId].cartLines) {
                        addAllToWishlistDataLayer(cartStateUpdated[cartId].cartLines);
                    }
                }
            } else if (window.location.href.includes("myaccount/orders/details")) {
                if (productInfos.length === 1 && orderNumber && orderInfo[orderNumber].orderLines) {
                    const currentList = orderInfo[orderNumber].orderLines?.slice();
                    const updatedList = currentList?.filter(item => item.productId === productInfos[0].productId);
                    addAllToWishListDataLayerOrderPage(updatedList);
                } else {
                    // eslint-disable-next-line no-lonely-if
                    if (orderNumber) {
                        const orders = orderInfo[orderNumber].orderLines;
                        addAllToWishListDataLayerOrderPage(orders);
                    }
                }
            }
        }
    };

    const returnUrl = location.pathname + location.search;

    return (
        <Modal
            {...styles.modal}
            headline={isAuthenticated ? translate("ADD TO LIST") : translate("Please Sign In")}
            isOpen={modalIsOpen}
            handleClose={modalCloseHandler}
        >
            <StyledWrapper {...styles.innerWrapper} data-test-selector="productAddToListModal">
                {!isAuthenticated && (
                    <Typography data-test-selector="productAddToListModal_requireSignIn" {...styles.signInMessageText}>
                        {siteMessage(
                            "Lists_Must_Sign_In_Spire",
                            `${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`,
                        )}
                    </Typography>
                )}
                {isAuthenticated && (
                    <>
                        <Typography {...styles.descriptionText}>
                            {translate("Add items to a new or existing list.")}
                        </Typography>
                        <DynamicDropdown
                            label={translate("Select List")}
                            {...styles.listsSelect}
                            isLoading={wishListsDataView.isLoading}
                            onInputChange={listsInputChangeHandler}
                            onSelectionChange={listChangeHandler}
                            selected={selectedWishListId}
                            options={options}
                            error={selectListError}
                            data-test-selector="productAddToListModal_listSelect"
                            disabled={options.length === 1 && selectedWishListId === "new"}
                        />
                        {selectedWishListId === "new" && (
                            <TextField
                                label={translate("New List Name")}
                                value={newListName}
                                error={newListNameError}
                                onChange={newListInputChangeHandler}
                                {...styles.newListInput}
                                data-test-selector="productAddToListModal_newName"
                            />
                        )}
                        <Checkbox
                            {...styles.scheduleReminderCheckbox}
                            checked={isScheduled}
                            onChange={scheduleReminderChangeHandler}
                        >
                            {siteMessage("Lists_Schedule_Reminders")}
                        </Checkbox>
                        <GridContainer {...styles.container}>
                            <GridItem {...styles.bottomRowGridItem}>
                                <Link
                                    {...styles.cancelButton}
                                    onClick={modalCloseHandler}
                                    data-test-selector="productShareCancel"
                                    hoverMode={undefined}
                                >
                                    {translate("Cancel")}
                                </Link>
                                <Button
                                    {...styles.addButton}
                                    onClick={e => addToListButtonClickHandler(e)}
                                    data-test-selector="productAddToListModal_addButton"
                                    disabled={inProgress}
                                >
                                    {translate("ADD TO LIST")}
                                </Button>
                            </GridItem>
                        </GridContainer>
                        {isScheduled && currentWishList && (
                            <ScheduleReminderModal
                                wishList={currentWishList}
                                isOpen={scheduleReminderModalIsOpen}
                                handleClose={closeScheduleReminderModalHandler}
                                extendedStyles={styles.scheduleReminderModal}
                            />
                        )}
                    </>
                )}
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(AddToListModal);
